<template>
  <component
    class="snow-button"
    :is="component"
    :to="to"
    :type="type"
    :style="style"
    :class="classes"
    :disabled="_disabled"
    @click.prevent="$emit('click')"
  >
    <i v-if="icon" :class="[icon, `fs-${iconSize}`]" />
    <span class="button-text" v-if="$slots.default || text">
      <slot>{{ text }}</slot>
    </span>
    <i v-if="rightIcon" :class="[rightIcon, `fs-${iconSize}`]" />
  </component>
</template>

<script>
export default {
  props: {
    size: {
      type: String,
      default: "md",
      validator: value => ["sm", "md", "lg"].includes(value)
    },
    text: {
      type: String,
      default: ""
    },
    variant: {
      type: String,
      default: "filled",
      validator: value =>
        [
          "filled",
          "outline",
          "gray",
          "borderless",
          "danger",
          "positive"
        ].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ""
    },
    rightIcon: {
      type: String,
      default: ""
    },
    to: {
      type: [String, Object],
      default: ""
    },
    type: {
      type: String,
      default: "button"
    }
  },
  data() {
    return {
      component: this.to ? "router-link" : "button"
    };
  },
  methods: {
    getHeight() {
      return {
        sm: "28px",
        md: "40px",
        lg: "56px"
      }[this.size];
    },
    getLineHeight() {
      return {
        sm: "20px",
        md: "24px",
        lg: "28px"
      }[this.size];
    },

    getFontSize() {
      return {
        sm: "fs-14",
        md: "fs-16",
        lg: "fs-18"
      }[this.size];
    },
    getPadding() {
      if (!this.text)
        return {
          sm: "p-1",
          md: "p-2",
          lg: "p-3"
        }[this.size];
      return {
        sm: "px-2 py-1",
        md: "px-4 py-2",
        lg: "px-6 py-3"
      }[this.size];
    },
    getGap() {
      return {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-2"
      }[this.size];
    },
    getBorderRadius() {
      return {
        sm: "rounded-8",
        md: "rounded-12",
        lg: "rounded-16"
      }[this.size];
    },
    getTextColor() {
      return {
        borderless: "text-dark",
        gray: "text-dark",
        outline: "text-dark",
        filled: "text-light",
        danger: "text-light",
        positive: "text-light"
      }[this.variant];
    },
    getBackgroundColor() {
      return {
        gray: "bg-dark-4",
        filled: "bg-blue",
        danger: "bg-danger",
        positive: "bg-positive"
      }[this.variant];
    },
    getHoverBackgroundColor() {
      return {
        borderless: "hover__bg-dark-4",
        gray: "hover__bg-dark-20",
        outline: "hover__bg-dark-4",
        positive: "hover__bg-positive-80"
      }[this.variant];
    }
  },

  computed: {
    _disabled() {
      return this.disabled;
    },
    style() {
      return {
        "--height": this.getHeight(),
        "--line-height": this.getLineHeight()
      };
    },
    classes() {
      let classes = [
        this.getPadding(),
        this.getGap(),
        this.getBorderRadius(),
        this.getFontSize(),
        this.getTextColor(),
        this.getBackgroundColor(),
        this.getHoverBackgroundColor()
      ];

      if (this.variant == "outline") {
        classes.push("border-dark-10 hover__border-dark-20");
      }
      if (this.block) {
        classes.push("snow-button--block");
      }

      return classes;
    },
    iconSize() {
      return {
        sm: 16,
        md: 20,
        lg: 24
      }[this.size];
    }
  }
};
</script>

<style lang="scss" scoped>
.snow-button {
  align-items: center;
  border: 1px solid transparent;
  display: flex;
  justify-content: center;
  background-color: transparent;
  text-decoration: none;
  white-space: nowrap;

  height: var(--height);

  transition: 0.2s all;

  &.bg-brand {
    background-color: var(--brand);
    &:hover {
      background-color: color-mix(
        in srgb,
        var(--brand) 60%,
        transparent
      ) !important;
    }
  }
  &:disabled {
    cursor: not-allowed;
    color: var(--dark-20) !important;
    background-color: var(--dark-4) !important;
    &:hover {
      background-color: var(--dark-4) !important;
    }
  }
  &--block {
    width: 100%;
  }

  span.button-text {
    line-height: var(--line-height);
    transition: padding 0.2s;
  }

  &:hover:not(:disabled) {
    span.button-text {
      padding: 0 4px;
    }
  }
}
</style>
